import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Paper, Typography, useTheme, Stack, Divider } from '@mui/material'
import EMobilityWallboxTriggerMenu from './EMobilityWallboxTriggerMenu'
import { makeStyles } from 'tss-react/mui'
import { WallboxHardwareModel } from 'src/Libs/wallboxes/models'
import WallboxPriceInfo from './WallboxPriceInfo'
import { useFetchWallboxPrices } from 'src/Libs/e-mobility/charging-sessions'

const useStyles = makeStyles()(theme => ({
  container: {
    padding: theme.spacing(4),
    paddingTop: theme.spacing(0),
  },
  titleContainer: {
    padding: theme.spacing(1),
    paddingRight: theme.spacing(0),
    borderLeft: `4px solid ${theme.palette.background.paperAccent}`,
  },
  priceInfoContainer: {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.shades.shade04,
    marginTop: theme.spacing(3),
  },
  divider: {
    borderColor: theme.palette.shades.shade03,
  },
}))

interface EMobilityWallboxCardProps {
  isDynamicPrice: boolean
  wallboxId: string
  title: string
  storageId: string
  wallboxIndex: number
  wallboxType: number
  wallboxes: WallboxHardwareModel[]
}

const EMobilityWallboxCard: React.FC<EMobilityWallboxCardProps> = ({
  isDynamicPrice,
  wallboxId,
  title,
  storageId,
  wallboxIndex,
  wallboxType,
  wallboxes,
}): JSX.Element => {
  const { classes } = useStyles()
  const theme = useTheme()
  const { t } = useCustomTranslation()

  const { chargePrices, dischargePrices, isChargePricesError, isChargePricesLoading, isDischargePricesError, isDischargePricesLoading } =
    useFetchWallboxPrices({ serialNo: storageId, wallboxIndex })

  /* we are displaying discharge price only if wallbox type is edison connect
   * only wallbox with type 5 is edison connect
   */
  const isEdisonConnect = wallboxType === 5

  return (
    <Paper className={classes.container}>
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} className={classes.titleContainer}>
        <Typography variant={theme.typography.dialog} color="textPrimary">
          {t('emobility.wallbox.cardTitle', { wallbox: title, id: wallboxId })}
        </Typography>
        <EMobilityWallboxTriggerMenu
          wallboxId={wallboxId}
          title={title}
          chargePrices={chargePrices || []}
          dischargePrices={dischargePrices || []}
          storageId={storageId}
          wallboxIndex={wallboxIndex}
          isEdisonConnect={isEdisonConnect}
          wallboxes={wallboxes}
        />
      </Stack>

      <Stack className={classes.priceInfoContainer}>
        <WallboxPriceInfo
          isDynamicPrice={isDynamicPrice}
          prices={chargePrices}
          isLoading={isChargePricesLoading}
          isError={isChargePricesError}
          energyDirection="charge"
        />
        {isEdisonConnect && <Divider className={classes.divider} />}
        {isEdisonConnect && (
          <WallboxPriceInfo
            prices={dischargePrices}
            isLoading={isDischargePricesLoading}
            isError={isDischargePricesError}
            energyDirection="discharge"
          />
        )}
      </Stack>
    </Paper>
  )
}

export default EMobilityWallboxCard
